import { Trans } from 'react-i18next';
import { useMemo, useState } from 'react';

import { Alert } from 'componets/ui';
import { BearLossCalculatorModal } from 'pages/Account/Overview/BearLossCalculator/BearLossCalculatorModal';

import { useAppSelector, useTranslate } from 'hooks';

import { ClientRole, Paths, RequestStatus, VerificationStatus } from 'utils/constants';

import Utils from 'utils/utils';

import { selectUser } from 'selectors';

import styles from '../index.module.scss';

const WORDS = [
	'identifyIsNotVerifiedText',
	'verifyNowText',
	'taxResidencyNotSpecifiedText',
	'specifyNowText',
	'quizStatusText',
	'startText',
	'alertRiskToleranceText',
	'verifyText',
	'alertIdentityExpiredText',
	'alertCompanyExpiredText',
	'alertCompanyExpiredSoonText',
	'alertIdentityExpiredSoonText',
	'alertCompanyVerificationText',
	'readMoreText',
	'alertDASFormText',
	'alertIdentityNullText',
	'alertIdentityNullCompanyText',
];

const LT = 'LT';

export const AccountLayoutContentAlerts = () => {
	const user = useAppSelector(selectUser);

	const { documents, status } = useAppSelector((state) => state.overview);

	const t = useTranslate(WORDS);

	const [isOpenRiskTolerance, setIsOpenRiskTolerance] = useState(false);

	const { verificationStatus } = user;

	const isIdentityActive = useMemo(
		() => Utils.isDocumentActive(documents.identity),
		[documents.identity],
	);

	const isIdentityExpiredSoon = useMemo(
		() => Utils.isDocumentExpiredSoon(documents.identity),
		[documents.identity],
	);
	const isIdentityNull = useMemo(
		() => Utils.isDocumentNull(documents.identity),
		[documents.identity],
	);

	const isIdentityCompanyActive = useMemo(
		() => Utils.isDocumentActive(documents.company),
		[documents.company],
	);

	const isIdentityCompanyExpiredSoon = useMemo(
		() => Utils.isDocumentExpiredSoon(documents.company),
		[documents.company],
	);

	const isIdentityCompanyNull = useMemo(
		() => Utils.isDocumentNull(documents.company),
		[documents.company],
	);

	const isFailedVeriff = verificationStatus === VerificationStatus.FAILED;

	const isDASAlertDecember =
		user.userType === ClientRole.USER &&
		user.taxResidence?.country.iso !== LT &&
		documents.das1?.length > 0 &&
		Utils.isDateInDecember(new Date());

	const isDASAlert =
		user.taxResidence &&
		user.userType === ClientRole.USER &&
		user.taxResidence?.country.iso !== LT &&
		!documents.das1;

	const isCompany = user.userType === ClientRole.COMPANY;

	const isExpiredIdentity = documents.identity && !isIdentityActive && !isIdentityNull;

	const isLoading = status === RequestStatus.PENDING;

	if (isLoading) return null;

	return (
		<>
			{verificationStatus !== VerificationStatus.VERIFIED && !isExpiredIdentity && (
				<Alert
					text={
						isFailedVeriff ? (
							<Trans
								i18nKey="messageVerificationFailedText"
								components={{
									l: (
										<a
											href="mailto:info@crowdpear.com"
											target="_blank"
											className={styles.link}
											rel="noreferrer"
										/>
									),
								}}
							/>
						) : (
							t.identifyIsNotVerifiedText
						)
					}
					path={isFailedVeriff ? '' : Paths.VERIFY_IDENTITY}
					linkText={t.verifyNowText}
				/>
			)}

			{!user.taxResidence && (
				<Alert
					text={t.taxResidencyNotSpecifiedText}
					path={Paths.RESIDENCY}
					linkText={t.specifyNowText}
				/>
			)}

			{(!user.quizStatuses?.KYC || !user.quizStatuses?.AML) && (
				<Alert
					text={t.quizStatusText}
					path={user.quizStatuses?.KYC ? Paths.AML : Paths.KYC}
					linkText={t.startText}
				/>
			)}

			{!user.riskTolerance?.value && (
				<Alert
					text={t.alertRiskToleranceText}
					path=""
					linkText={t.startText}
					isButton
					onClick={() => setIsOpenRiskTolerance(true)}
				/>
			)}

			<BearLossCalculatorModal
				isOpen={isOpenRiskTolerance}
				onClose={() => setIsOpenRiskTolerance(false)}
			/>

			{isExpiredIdentity && (
				<Alert
					text={t.alertIdentityExpiredText}
					linkText={t.verifyText}
					path={Paths.VERIFY_IDENTITY}
				/>
			)}

			{documents.identity && isIdentityExpiredSoon && isIdentityActive && (
				<Alert text={t.alertIdentityExpiredSoonText} />
			)}

			{documents.company && !isIdentityCompanyActive && !isIdentityCompanyNull && (
				<Alert text={t.alertCompanyExpiredText} />
			)}

			{documents.company && isIdentityCompanyExpiredSoon && isIdentityCompanyActive && (
				<Alert text={t.alertCompanyExpiredSoonText} />
			)}

			{isCompany && !documents.company && <Alert text={t.alertCompanyVerificationText} />}

			{isIdentityNull && documents.identity && <Alert text={t.alertIdentityNullText} />}

			{isIdentityCompanyNull && documents.company && (
				<Alert text={t.alertIdentityNullCompanyText} />
			)}

			{isDASAlertDecember && (
				<Alert
					target="_blank"
					linkText={t.readMoreText}
					path="https://crowdpear.com/help/what-documents-do-i-need-to-submit-to-reduce-the-taxes-paid-in-my-own-country/"
					text={
						<Trans
							i18nKey="alertDASFormDecemberText"
							values={{ year: new Date().getFullYear() + 1 }}
						/>
					}
				/>
			)}

			{isDASAlert && (
				<Alert
					target="_blank"
					linkText={t.readMoreText}
					path="https://crowdpear.com/help-category/investments"
					text={t.alertDASFormText}
				/>
			)}
		</>
	);
};
